<!--
    The month grid of PlanupCalendar.vue (and its 2-week strip on the dashboard: period "week"):
    a grid of day cells, each with its number and Hebrew date and the day's items as tinted pills
    (icon, title, time, and in a wide calendar a second line with the customer / type / place), then
    "ועוד N" when there are more than maxItems. It replaced vue-simple-calendar: items are laid out
    per day (a multi-day event is repeated on each of its days), nothing is positioned by hand.

    Props in, events out: click-date(Date), click-item(item), show-more(Date), swipe('prev'|'next'),
    item-enter(item, element) / item-leave for the hover card. selectedDates marks days as chosen (a
    tint and a check - the event date picker's several options). The pills' colors come from the CSS
    variables of calendar-colors.js (--pc-rgb / --pc-ink / --pc-alpha...), set by PlanupCalendar's
    stylesheet per item type and status and overridden by the account's chosen colors.

    Layout modes by the grid's own width: compact (a phone: two short pills a day, no Hebrew dates,
    no icons) and detailed (two-line pills); the parent can turn the second line off with `simple`.
-->
<template>
    <div class="pcal-month"
         :class="{ compact: compact, narrow: narrow, detailed: detailed, 'pcal-month-clicks-off': disableItemClicks }"
         :style="{ minHeight: minHeight }"
         @touchstart.passive="onTouchStart"
         @touchend.passive="onTouchEnd">

        <div class="pcal-weekdays">
            <div v-for="(name, index) in dayNames"
                 :key="index">
                {{ name }}
            </div>
        </div>

        <div class="pcal-weeks">
            <div class="pcal-week"
                 v-for="week in weeks"
                 :key="week.key">

                <div class="pcal-day"
                     v-for="day in week.days"
                     :key="day.key"
                     :class="{ today: day.isToday, outside: day.outside, past: day.past, weekend: day.weekend, selected: day.selected }"
                     :data-date="day.iso"
                     @click="$emit('click-date', day.date)">

                    <div class="pcal-day-head">
                        <span class="pcal-day-num">{{ day.number }}</span>
                        <span class="pcal-day-heb"
                              v-if="day.hebrew">{{ day.hebrew }}</span>
                        <span class="pcal-day-check"
                              v-if="day.selected"><i class="fa-solid fa-check"></i></span>
                    </div>

                    <div class="pcal-day-items">
                        <div v-for="entry in day.entries"
                             :key="entry.uid"
                             class="pcal-item"
                             :class="[entry.item.itemType].concat(entry.item.colorClasses || [], entry.span ? ['pcal-item-span'] : [])"
                             :data-item-id="entry.item.id"
                             @click.stop="clickEntry(entry, day)"
                             @mouseenter="$emit('item-enter', entry.item, $event.currentTarget)"
                             @mouseleave="$emit('item-leave')">

                            <span class="pcal-item-icon">
                                <i :class="iconOf(entry.item)"></i>
                            </span>

                            <span class="pcal-item-main">
                                <span class="pcal-item-title">{{ titleOf(entry) }}</span>
                                <span class="pcal-item-sub"
                                      v-if="detailed && entry.item.subtitle">{{ entry.item.subtitle }}</span>
                            </span>

                            <span class="pcal-item-time"
                                  v-if="entry.item.time">{{ entry.item.time }}</span>

                            <span class="pcal-item-crew"
                                  v-if="showCrew && crewStyle(entry.item)"
                                  :style="crewStyle(entry.item)"></span>
                        </div>
                    </div>

                    <div class="pcal-more"
                         v-if="day.hidden"
                         @click.stop="$emit('show-more', day.date)">
                        ועוד {{ day.hidden }}
                    </div>

                </div>

            </div>
        </div>

    </div>
</template>
<script>
import { itemIcon } from '../../services/calendar/calendar-colors';
import { keyOfLocal, itemDayRange, utcOfKey, isoOfLocal, minutesOf } from '../../services/calendar/calendar-days';

const DAY_NAMES = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
const DAY_NAMES_SHORT = ["א'", "ב'", "ג'", "ד'", "ה'", "ו'", "ש'"];

const MAX_SPAN_DAYS = 60;

export default {
    name: 'CalendarMonthGrid',
    props: {
        // the date the grid shows: its month, or (period "week") its week and the periodCount - 1 after it
        date: { default: null },
        period: { default: 'month' },
        periodCount: { default: 1 },
        items: { default: () => [] },
        maxItems: { default: 3 },
        showCrew: { default: false },
        showHebrewDates: { default: true },
        // never the two-line pills (a small calendar)
        simple: { default: false },
        minHeight: { default: null },
        // a click on a pill is a click on its day (the date picker)
        disableItemClicks: { default: false },
        // the chosen days (Dates or timestamps, any time of the day)
        selectedDates: { default: () => [] },
    },
    data() {
        return {
            width: 0,
            touch: null,
            hebrewCache: {},
        };
    },
    computed: {

        anchor() {
            const date = this.date ? new Date(this.date) : new Date();
            return isNaN(date.getTime()) ? new Date() : date;
        },

        compact() {
            return this.width > 0 && this.width < 620;
        },

        // cells under ~115px (the יומן modal on a small laptop): the title gets the icon's room
        narrow() {
            return this.width > 0 && this.width < 820 && !this.compact;
        },

        detailed() {
            return !this.simple && this.width >= 860;
        },

        effectiveMax() {
            return this.compact ? 2 : Math.max(1, parseInt(this.maxItems) || 3);
        },

        dayNames() {
            return this.compact ? DAY_NAMES_SHORT : DAY_NAMES;
        },

        selectedKeys() {
            const keys = {};
            (this.selectedDates || []).forEach(value => {
                const date = new Date(value);
                if (!isNaN(date.getTime()))
                    keys[keyOfLocal(date)] = true;
            });
            return keys;
        },

        // the items of each day, sorted: all-day first, then by time (ties keep the given order)
        itemsByDay() {

            const byDay = {};
            const items = this.items || [];

            for (let i = 0; i < items.length; i++) {

                const item = items[i];
                if (!item || item.itemType == 'showMore')
                    continue;

                const range = itemDayRange(item);
                if (!range)
                    continue;

                const total = Math.min(MAX_SPAN_DAYS, Math.round((utcOfKey(range.end) - utcOfKey(range.start)) / 86400000) + 1);
                const cursor = new Date(utcOfKey(range.start));

                for (let n = 0; n < total; n++) {

                    const key = cursor.getUTCFullYear() * 10000 + (cursor.getUTCMonth() + 1) * 100 + cursor.getUTCDate();

                    if (!byDay[key])
                        byDay[key] = [];

                    byDay[key].push({
                        item: item,
                        uid: item.id + '_' + key,
                        span: total > 1 ? { index: n + 1, total: total } : null,
                        sort: minutesOf(item.time),
                        order: i,
                    });

                    cursor.setUTCDate(cursor.getUTCDate() + 1);
                }
            }

            Object.keys(byDay).forEach(key => {
                byDay[key].sort((a, b) => (a.sort - b.sort) || (a.order - b.order));
            });

            return byDay;
        },

        weeks() {

            const anchor = this.anchor;
            const todayKey = keyOfLocal(new Date());
            let start, count;

            if (this.period == 'week') {
                start = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate() - anchor.getDay());
                count = Math.max(1, parseInt(this.periodCount) || 1);
            } else {
                const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
                const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
                start = new Date(first.getFullYear(), first.getMonth(), 1 - first.getDay());
                count = Math.ceil((first.getDay() + daysInMonth) / 7);
            }

            const byDay = this.itemsByDay;
            const selected = this.selectedKeys;
            const max = this.effectiveMax;
            const hebrew = this.showHebrewDates && !this.compact;
            const weeks = [];

            for (let w = 0; w < count; w++) {

                const days = [];

                for (let i = 0; i < 7; i++) {

                    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + w * 7 + i);
                    const key = keyOfLocal(date);
                    const entries = byDay[key] || [];
                    const visible = entries.length > max ? entries.slice(0, max) : entries;

                    days.push({
                        key: key,
                        iso: isoOfLocal(date),
                        date: date,
                        number: date.getDate(),
                        hebrew: hebrew ? this.hebrewLabel(date) : '',
                        isToday: key == todayKey,
                        outside: this.period != 'week' && date.getMonth() != anchor.getMonth(),
                        past: key < todayKey,
                        weekend: i == 6,
                        selected: !!selected[key],
                        entries: visible,
                        hidden: entries.length - visible.length,
                    });
                }

                weeks.push({ key: 'w' + w, days: days });
            }

            return weeks;
        },
    },
    methods: {

        // כ"ה בתשרי
        hebrewLabel(date) {
            const key = keyOfLocal(date);
            if (this.hebrewCache[key] !== undefined)
                return this.hebrewCache[key];
            let label = '';
            try {
                const dayAndMonth = planup.$globals.hebrewDateDay(date);
                label = dayAndMonth ? dayAndMonth.replace(' ', ' ב') : '';
            } catch (e) {
                label = '';
            }
            this.hebrewCache[key] = label;
            return label;
        },

        iconOf(item) {
            return itemIcon(item.itemType);
        },

        titleOf(entry) {
            const title = entry.item.originalTitle || entry.item.title || 'ללא שם';
            return entry.span ? `${title} (${entry.span.index}/${entry.span.total})` : title;
        },

        // the crew-members strip: item-mapper paints the crew colors as a gradient
        crewStyle(item) {
            return item.style && item.style.indexOf('linear-gradient(to right, #') > -1 ? item.style : null;
        },

        clickEntry(entry, day) {
            if (this.disableItemClicks)
                this.$emit('click-date', day.date);
            else
                this.$emit('click-item', entry.item);
        },

        measure() {
            this.width = this.$el ? this.$el.getBoundingClientRect().width : 0;
        },

        onTouchStart(event) {
            const touch = event.touches && event.touches[0];
            this.touch = touch ? { x: touch.clientX, y: touch.clientY } : null;
        },

        // a sideways swipe moves a period: the finger going right reveals the next one (the left arrow)
        onTouchEnd(event) {
            const touch = event.changedTouches && event.changedTouches[0];
            if (!touch || !this.touch)
                return;
            const dx = touch.clientX - this.touch.x;
            const dy = touch.clientY - this.touch.y;
            this.touch = null;
            if (Math.abs(dx) > 60 && Math.abs(dy) < 50 && Math.abs(dx) > Math.abs(dy) * 1.5)
                this.$emit('swipe', dx > 0 ? 'next' : 'prev');
        },
    },
    mounted() {
        this.measure();
        if (window.ResizeObserver) {
            this.resizeObserver = new ResizeObserver(() => this.measure());
            this.resizeObserver.observe(this.$el);
        } else {
            window.addEventListener('resize', this.measure);
        }
    },
    beforeDestroy() {
        if (this.resizeObserver)
            this.resizeObserver.disconnect();
        window.removeEventListener('resize', this.measure);
    },
}
</script>
<style>
.pcal-month {
    direction: rtl;
    display: flex;
    flex-direction: column;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    overflow: hidden;
    user-select: none;
}

.pcal-weekdays {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
}

.pcal-weekdays>div {
    padding: 9px 4px;
    text-align: center;
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    white-space: nowrap;
    overflow: hidden;
}

.pcal-weekdays>div+div {
    border-right: 1px solid #e2e8f0;
}

.pcal-weeks {
    display: flex;
    flex-direction: column;
    flex: 1;
}

.pcal-week {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    flex: 1;
    min-height: 122px;
    border-bottom: 1px solid #e2e8f0;
}

.pcal-month.detailed .pcal-week {
    min-height: 150px;
}

.pcal-week:last-child {
    border-bottom: 0;
}

.pcal-day {
    position: relative;
    min-width: 0;
    display: flex;
    flex-direction: column;
    padding: 6px 6px 4px;
    border-right: 1px solid #e2e8f0;
    cursor: pointer;
    transition: background-color .12s;
}

.pcal-day:first-child {
    border-right: 0;
}

.pcal-day:hover {
    background: #f8fafc;
}

.pcal-day.outside {
    background: #fafafa;
}

.pcal-day.outside .pcal-day-num {
    color: #b6bcc8;
}

.pcal-day.outside .pcal-day-heb {
    color: #c3c8d1;
}

.pcal-day.today {
    background: #eef5ff;
    box-shadow: inset 0 0 0 1.5px #8cb8f5;
    border-radius: 8px;
}

.pcal-day.today:hover {
    background: #e4efff;
}

.pcal-day.today .pcal-day-num {
    color: #2563eb;
}

/* a chosen day (after .today: a chosen today looks chosen) */
.pcal-day.selected {
    background: #f1ecfd;
    box-shadow: inset 0 0 0 2px #ab8ce4;
    border-radius: 8px;
}

.pcal-day.selected:hover {
    background: #e9e0fb;
}

.pcal-day.selected .pcal-day-num {
    color: #6d5bd0;
}

.pcal-day-check {
    flex: 0 0 20px;
    width: 20px;
    height: 20px;
    margin-right: auto;
    align-self: center;
    border-radius: 50%;
    background: #6d5bd0;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
}

.pcal-day-head {
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin-bottom: 4px;
    min-width: 0;
}

.pcal-day-num {
    font-size: 15px;
    font-weight: 700;
    line-height: 20px;
    color: #1f2937;
    flex: 0 0 auto;
}

.pcal-day-heb {
    font-size: 11.5px;
    color: #64748b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
}

.pcal-day-items {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
}

/* a pill: the tint, the ink and the look come from the --pc variables (calendar-colors.js) */
.pcal-item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 5px;
    min-height: 24px;
    padding: 2px 5px 2px 6px;
    border-radius: 6px;
    border: 1px solid rgba(var(--pc-rgb), var(--pc-border-alpha));
    background-color: rgba(var(--pc-rgb), calc(var(--pc-alpha) + var(--pc-hover)));
    background-image: repeating-linear-gradient(135deg, rgba(var(--pc-rgb), calc(.18 * var(--pc-stripe))) 0 4px, rgba(var(--pc-rgb), calc(.04 * var(--pc-stripe))) 4px 9px);
    color: var(--pc-ink);
    font-size: 12px;
    line-height: 1.25;
    cursor: pointer;
    overflow: hidden;
    transition: transform .12s, box-shadow .12s, background-color .12s;
}

.pcal-item:hover {
    --pc-hover: .1;
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(15, 23, 42, .12);
}

.pcal-month-clicks-off .pcal-item {
    cursor: default;
}

.pcal-item-icon {
    flex: 0 0 18px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: rgba(var(--pc-rgb), .2);
    color: var(--pc-ink);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
}

.pcal-item-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
}

.pcal-item-title {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: 600;
}

.pcal-item-sub {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 11px;
    font-weight: 400;
    opacity: .82;
}

.pcal-item-time {
    flex: 0 0 auto;
    font-size: 11px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    direction: ltr;
}

.pcal-item-crew {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 4px;
}

.pcal-more {
    margin-top: auto;
    padding-top: 3px;
    text-align: center;
    font-size: 11.5px;
    color: #64748b;
    cursor: pointer;
}

.pcal-more:hover {
    color: #1f2937;
    text-decoration: underline;
}

/* narrow cells: no icon circle, the title keeps the room */
.pcal-month.narrow .pcal-item-icon {
    display: none;
}

.pcal-month.narrow .pcal-item {
    padding-right: 7px;
}

/* a phone */
.pcal-month.compact .pcal-weekdays>div {
    padding: 6px 2px;
    font-size: 11px;
}

.pcal-month.compact .pcal-week {
    min-height: 78px;
}

.pcal-month.compact .pcal-day {
    padding: 3px 3px 2px;
}

.pcal-month.compact .pcal-day.today {
    border-radius: 5px;
}

.pcal-month.compact .pcal-day-head {
    margin-bottom: 2px;
}

.pcal-month.compact .pcal-day-num {
    font-size: 12px;
    line-height: 16px;
}

.pcal-month.compact .pcal-day.selected {
    border-radius: 5px;
}

.pcal-month.compact .pcal-day-check {
    flex-basis: 14px;
    width: 14px;
    height: 14px;
    font-size: 8px;
}

.pcal-month.compact .pcal-day-items {
    gap: 2px;
}

.pcal-month.compact .pcal-item {
    min-height: 18px;
    padding: 1px 3px;
    border-radius: 4px;
    font-size: 10px;
    gap: 0;
}

.pcal-month.compact .pcal-item-icon,
.pcal-month.compact .pcal-item-sub,
.pcal-month.compact .pcal-item-time {
    display: none;
}

.pcal-month.compact .pcal-item:hover {
    transform: none;
    box-shadow: none;
}

.pcal-month.compact .pcal-more {
    font-size: 10px;
    padding-top: 1px;
}
</style>
